import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Hidden } from '@material-ui/core';
import { FormikHelpers } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useDependents } from '../../hooks/useDependents';
import { DependentForm } from './dependent-form';
import { DependentFormValues } from './dependent-form-models';
import { ChildTypes, DependentsState, DependentTypes } from './dependents-model';
import { getDependentByNumber } from './dependents-selectors';
import { handleDependentChange, handleSubmitChildPost, handleSubmitDomesticPartnerPost, handleSubmitPut } from './dependents-requests';
import { setPostDependentStatus } from './dependents-slice';
import { ApiStatuses, BaseApiState } from '../../../../global/models/api-model';
import { setPatchPersonStatus } from '../personal-info/personal-info-slice';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { setDocumentUploadStatus } from '../personal-info/document-upload-slice';
import { FormTypes } from '../../models/form-models';
import { DemographicAppKeys, DemographicsContext } from '../../../../global/models/demographics-model';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { PersonSearchContext, PersonSearchKeys, PersonSearchState, SearchPerson } from '../../../arc/features/person-search/person-search-model';
import { DocumentUploadState } from '../personal-info/document-upload-models';
import { AESteps, AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../annual-enrollment/models/annual-enrollment-models';
import { setAEStep } from '../../../annual-enrollment/annual-enrollment-slice';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { getGpsType } from '../../../../global/helpers/gps-helper';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { PersonalInfoPersonTypes } from '../personal-info/personal-info-models';
import { OSCRRService } from '../../../arc/features/person-search/oscrr-helpers';
import { Person } from '../../../../global/models/person-model';
import { ProcessName, RegisterStartProcess, RegisterCompletedProcess } from '../../../../global/helpers/analytics-helper';
import { clearBeneficiaries, clearBeneficiariesRelationships } from '../../../arc/features/beneficiary/beneficiary-slices';
import { clearMemberNewHireEnrollmentCoverage, clearNewHireEnrollmentCoverage } from '../../../arc/features/new-hire-enrollment/slice/new-hire-enrollment-slices';
import { AccountInfoRoutes } from '../../constants/routes';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { useQuery } from '../../../../global/hooks/useQuery';
import { MaritalStatuses } from '../../../../global/constants/lists';

interface Props {
	relationshipsPageRoute: string;
	skipDocUpload?: boolean;
}
export function DependentInfo(props: Props) {
	const apiState = useSelector<TcsmStates, BaseApiState>(state => state.apiState);
	const personSearchKey = PersonSearchKeys.Dependents;
	const personSearchState = useSelector<TcsmStates, PersonSearchState>(state => state.personSearchState[personSearchKey]);

	const app = useContext(DemographicsContext);
	const enrollmentContext = useContext(AnnualEnrollmentContext);
	const isAnnualEnrollment = enrollmentContext === AnnualEnrollmentKey.annualEnrollment || enrollmentContext === AnnualEnrollmentKey.participantEnrollment || enrollmentContext === AnnualEnrollmentKey.newHireEnrollment || enrollmentContext === AnnualEnrollmentKey.dioApproverEnrollment;
	const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);
	const dependentsState = useSelector<TcsmStates, DependentsState>(state => state.dependents[app]);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const documentUploadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[app]);
	const dependentsLoaded = useDependents(personGuid);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const currentPerson = useSelector<TcsmStates, Person>(state => state.personalInfo[app]?.currentPerson);
	const history = useHistory();
	const { state } = useLocation<any>();
	const myCpgMidYearPath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.Midyear}${AccountInfoRoutes.PersonalInfoSegment}`);

	const dependent = getDependentByNumber(dependentsState.dependents, state?.dependentNumber);
	const dependentPerson = dependent?.person;
	const isAdd = isEmpty(dependent);

	let childType = useQuery()?.get('childType');

	if (childType === ChildTypes.step && currentPerson?.marital_status !== MaritalStatuses.Married) {
		childType = ChildTypes.legal;
	}

	const gpsType = isAdd ? GpsType.ADD_DEPENDENT : GpsType.EDIT_DEPENDENT;
	const gpsItem = getGpsType(gpsType, app);

	const dispatch = useDispatch();
	const pageRef = useRef<HTMLDivElement>(null);

	RegisterStartProcess(isAdd ? ProcessName.DependentAdd : ProcessName.DependentUpdate);

	useEffect(() => {
		dispatch(apiErrorClear());
		dispatch(setDocumentUploadStatus(false, app));
		dispatch(setPostDependentStatus(ApiStatuses.None, app));
		dispatch(setPatchPersonStatus(ApiStatuses.None, app));
	}, []);

	useEffect(() => {
		if (isAnnualEnrollment) {
			dispatch(setAEStep(AESteps.Relationships, app));
		}
	}, []);

	if (!dependentsLoaded) {
		return <LoadingCircle />;
	}

	const onSubmitHandler = async (values: DependentFormValues, savedValues: DependentFormValues, formHelpers: FormikHelpers<DependentFormValues>, selectedPerson: SearchPerson) => {

		// Check if new results were found after click.
		if (await OSCRRService.SearchHasNewResultsAsync(apiState, personSearchState)) return;

		const formSuccessAction = () => {
			formHelpers.resetForm();
			RegisterCompletedProcess(isAdd ? ProcessName.DependentAdd : ProcessName.DependentUpdate);
			dispatch(clearBeneficiaries());
			dispatch(clearBeneficiariesRelationships());
			dispatch(clearMemberNewHireEnrollmentCoverage());
			dispatch(clearNewHireEnrollmentCoverage());
		};

		// Adjust address mask.
		const toUpdate: DependentFormValues = {
			...values,
			mailing_address_1: values.mailing_address_1 === dependentPerson?.masked_mailing_address_1 ? (dependentPerson.masked_mailing_address_1 || '') : values.mailing_address_1,
			mailing_address_2: values.mailing_address_2 === dependentPerson?.masked_mailing_address_2 ? (dependentPerson.masked_mailing_address_2 || '') : values.mailing_address_2,
			mailing_address_3: values.mailing_address_3 === dependentPerson?.masked_mailing_address_3 ? (dependentPerson.masked_mailing_address_3 || '') : values.mailing_address_3,
		};

		if (isAdd) {
			// go to confirmation page
			if (app === DemographicAppKeys.arc && !showConfirmation) {
				setShowConfirmation(true);
				window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
				dispatch(apiErrorClear());
				formHelpers.setTouched({});
				formHelpers.setSubmitting(false);

				return;
			}

			if (selectedPerson) {
				handleSubmitPut(app, dispatch, personGuid, selectedPerson.guid, toUpdate, documentUploadState);
			} else if (toUpdate.dependent_type === DependentTypes.child) {
				handleSubmitChildPost(app, dispatch, history, personGuid, savedValues, toUpdate, documentUploadState, myCpgMidYearPath, props?.relationshipsPageRoute, formSuccessAction);
			} else {
				handleSubmitDomesticPartnerPost(app, dispatch, personGuid, savedValues, toUpdate, documentUploadState, formSuccessAction);
			}
		} else {
			const personType = (toUpdate.dependent_type === DependentTypes.child) ? PersonalInfoPersonTypes.Child : PersonalInfoPersonTypes.DomesticPartner;
			handleDependentChange(app, dispatch, dependent.person_guid, savedValues, toUpdate, documentUploadState, personGuid, personType,  props?.skipDocUpload);
		}

		setShowConfirmation(false);
	};

	const patchFormType = app === DemographicAppKeys.arc ? FormTypes.MAP_PATCH_DEPENDENT : FormTypes.PATCH_DEPENDENT;
	const postFormType = app === DemographicAppKeys.arc ? FormTypes.MAP_POST_DEPENDENT : FormTypes.POST_DEPENDENT;
	// redirect to my relationships page on success with query string
	if (dependentsState?.postDependentStatus === ApiStatuses.Success || dependentsState?.patchDependentStatus === ApiStatuses.Success) {
		return (<Redirect to={{
			pathname: props.relationshipsPageRoute,
			search: `?success=${dependentsState.postDependentStatus === ApiStatuses.Success ? postFormType : patchFormType}`,
		}} />);
	}

	const relationshipProps = epiProps?.relationshipsSettingState?.epiContent;
	return (

		<PageContainer>
			<MainContent fullWidth={isAnnualEnrollment} >

				<div ref={pageRef}>
					{
						isAnnualEnrollment && app === DemographicAppKeys.arc &&
						<ContentSpacer>
							<div dangerouslySetInnerHTML={{ __html: epiProps?.annualEnrollmentSettingsState?.epiContent?.adminAccessMessage }} />
						</ContentSpacer>
					}
					<PersonSearchContext.Provider value={PersonSearchKeys.Dependents}>
						{(OSCRRService.IsOSCRRWorking(apiState) ? <LoadingCircle /> : null)}
						<DependentForm
							formHeader={isAdd ? relationshipProps?.addDependentTitle : relationshipProps?.editDependentTitle}
							formDescription={isAdd ? relationshipProps?.addNewDependentDescription : relationshipProps.editExistingDependentDescription}
							dependent={dependent}
							dependentType={state?.dependentType || DependentTypes.child}
							relationshipsProps={epiProps.relationshipsSettingState.epiContent}
							labelSetting={epiProps.labelSettingState.epiContent}
							onSubmitHandler={onSubmitHandler}
							isAdd={isAdd}
							previousRoute={props.relationshipsPageRoute}
							showConfirmation={showConfirmation}
							setShowConfirmation={setShowConfirmation}
							currentPerson={currentPerson}
							childType={childType}
                            skipDocUpload={props?.skipDocUpload}
						/>
					</PersonSearchContext.Provider>

				</div>
			</MainContent>
			{
				!isAnnualEnrollment &&
				<Hidden smDown>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
					</SideBarContent>
				</Hidden>
			}
		</PageContainer>
	);
}


DependentInfo.defaultProps = {

	skipDocUpload: false,
};